import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="pay-schedule-form"
export default class extends Controller {
  static targets = [
    'frequency',
    'firstDay',
    'secondDay',
    'referencePayDate',
    'monthlyDeductionCountMax',
  ]

  declare readonly frequencyTarget: HTMLSelectElement
  declare readonly firstDayTarget: HTMLSelectElement
  declare readonly secondDayTarget: HTMLSelectElement
  declare readonly referencePayDateTarget: HTMLInputElement
  declare readonly monthlyDeductionCountMaxTarget: HTMLSelectElement
  declare readonly hasFirstDayTarget: boolean
  declare readonly hasSecondDayTarget: boolean
  declare readonly hasReferencePayDateTarget: boolean
  declare readonly hasMonthlyDeductionCountMaxTarget: boolean

  connect (): void {
    this.updateFieldVisibility()
    this.updateDayOptions()
    this.updateMonthlyDeductionCountMaxOptions()
  }

  frequencyChanged (): void {
    this.updateFieldVisibility()
    this.updateDayOptions()
    this.updateMonthlyDeductionCountMaxOptions()
  }

  firstDayChanged (): void {
    if (this.hasSecondDayTarget && this.frequency === 'semi_monthly') {
      this.updateSecondDayOptions()
    }
  }

  private get frequency (): string {
    return this.frequencyTarget.value
  }

  private updateFieldVisibility (): void {
    // Handle first day field
    if (this.hasFirstDayTarget) {
      const requiresFirstDay = ['semi_monthly', 'monthly'].includes(this.frequency)
      this.firstDayTarget.closest('div')?.classList.toggle('hidden', !requiresFirstDay)
      this.firstDayTarget.required = requiresFirstDay
    }

    // Handle second day field
    if (this.hasSecondDayTarget) {
      const requiresSecondDay = this.frequency === 'semi_monthly'
      this.secondDayTarget.closest('div')?.classList.toggle('hidden', !requiresSecondDay)
      this.secondDayTarget.required = requiresSecondDay
    }

    // Handle reference pay date field
    if (this.hasReferencePayDateTarget) {
      const requiresReferenceDate = ['weekly', 'bi_weekly'].includes(this.frequency)
      this.referencePayDateTarget.closest('div')?.classList.toggle('hidden', !requiresReferenceDate)
      this.referencePayDateTarget.required = requiresReferenceDate
    }
  }

  private updateDayOptions (): void {
    if (this.hasFirstDayTarget) {
      const currentFirstDay = this.firstDayTarget.value
      this.firstDayTarget.innerHTML = ''

      // For semi-monthly, first day must be before 25
      const maxDay = this.frequency === 'semi_monthly' ? 24 : 31

      for (let i = 1; i <= maxDay; i++) {
        const option = document.createElement('option')
        option.value = i.toString()
        option.text = i.toString()
        this.firstDayTarget.appendChild(option)
      }

      // Restore previous value if valid
      if (parseInt(currentFirstDay) <= maxDay) {
        this.firstDayTarget.value = currentFirstDay
      }
    }

    if (this.hasSecondDayTarget) {
      this.updateSecondDayOptions()
    }
  }

  private updateSecondDayOptions (): void {
    const currentSecondDay = this.secondDayTarget.value
    this.secondDayTarget.innerHTML = ''

    const firstDay = parseInt(this.firstDayTarget.value) || 1

    // Second day must be after first day and before or on 31
    for (let i = firstDay + 1; i <= 31; i++) {
      const option = document.createElement('option')
      option.value = i.toString()
      option.text = i.toString()
      this.secondDayTarget.appendChild(option)
    }

    // Restore previous value if valid
    if (parseInt(currentSecondDay) > firstDay) {
      this.secondDayTarget.value = currentSecondDay
    }
  }

  private updateMonthlyDeductionCountMaxOptions (): void {
    if (!this.hasMonthlyDeductionCountMaxTarget) return

    const currentValue = this.monthlyDeductionCountMaxTarget.value
    this.monthlyDeductionCountMaxTarget.innerHTML = ''

    // Add the "Deduct on all periods" option
    const blankOption = document.createElement('option')
    blankOption.value = ''  // Empty string will be converted to null by Rails
    blankOption.text = 'Deduct on all periods'
    this.monthlyDeductionCountMaxTarget.appendChild(blankOption)

    let maxValue: number
    switch (this.frequency) {
      case 'monthly':
        maxValue = 1
        break
      case 'semi_monthly':
        maxValue = 2
        break
      case 'bi_weekly':
        maxValue = 3
        break
      case 'weekly':
        maxValue = 5
        break
      default:
        return
    }

    for (let i = 1; i <= maxValue; i++) {
      const option = document.createElement('option')
      option.value = i.toString()
      option.text = i.toString()
      this.monthlyDeductionCountMaxTarget.appendChild(option)
    }

    // Restore previous value if valid
    if (currentValue === '' || parseInt(currentValue) <= maxValue) {
      this.monthlyDeductionCountMaxTarget.value = currentValue
    } else {
      this.monthlyDeductionCountMaxTarget.value = ''  // Default to "Deduct on all periods" if current value is invalid
    }
  }
}
